<template>
  <div>
    <dx-grid
      :url="url"
      :columns="columns"
      :actions="actions"
      :title="title"
      :beforeSetupGrid="beforeSetupGrid"
      :onRowClicked="onRowClicked"
      navigationPath="onRowClicked"
      navigationField="onRowClicked"
      ref="grid"
    ></dx-grid>

    <!-- Process Request Dialog -->
    <v-dialog persistent v-model="processDialog" max-width="900">
      <v-card class="request-dialog">
        <v-card-title class="dialog-header py-2">
          <v-icon class="mr-3 pt-1" color="white" size="24"
            >mdi-message-alert</v-icon
          >
          <span class="dialog-title">Process Bus Change Request</span>
          <v-spacer></v-spacer>
          <v-btn class="white--text my-0" icon dark @click="closeDialog">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text class="pa-3">
          <!-- Recording Section - Compact -->
          <v-card outlined class="mb-3 pa-2 recording-card">
            <v-layout align-center>
              <div
                class="flex-grow-1"
                v-if="selectedRequest && selectedRequest.recordingURL"
              >
                <audio
                  ref="audioPlayer"
                  controls
                  :src="selectedRequest.recordingURL"
                  style="height: 30px;"
                ></audio>
              </div>
              <div v-else class="grey--text flex-grow-1">
                <v-icon small color="grey">mdi-microphone-off</v-icon>
                No message
              </div>
              <div class="caption grey--text ml-3" v-if="selectedRequest">
                <v-icon x-small class="mr-1">mdi-phone-incoming</v-icon>
                {{
                  formatPhone(
                    selectedRequest.callerID || selectedRequest.CallerID
                  ) || "Unknown"
                }}
                <span
                  v-if="selectedRequest.date || selectedRequest.Date"
                  class="ml-2"
                >
                  |
                  {{ formatDate(selectedRequest.date || selectedRequest.Date) }}
                </span>
              </div>
            </v-layout>
          </v-card>

          <!-- Two Column Layout -->
          <v-layout row wrap>
            <!-- Left Column: Student Info -->
            <v-flex xs12 md5 class="pr-2">
              <div class="section-header mb-2">
                <v-icon x-small class="mr-1">mdi-account</v-icon>
                Student Info
              </div>
              <v-card outlined class="pa-2 compact-info" v-if="selectedRequest">
                <div class="info-row">
                  <span class="info-label">Name:</span>
                  <span class="info-value">{{
                    selectedRequest.studentName
                  }}</span>
                </div>
                <div class="info-row" v-if="selectedRequest.studentNameHeb">
                  <span class="info-label">Hebrew:</span>
                  <span class="info-value">{{
                    selectedRequest.studentNameHeb
                  }}</span>
                </div>
                <div class="info-row">
                  <span class="info-label">ID:</span>
                  <span class="info-value">{{
                    selectedRequest.studentID
                  }}</span>
                </div>
                <div class="info-row">
                  <span class="info-label">Class:</span>
                  <span class="info-value"
                    >{{ selectedRequest.building }} -
                    {{ selectedRequest.class }} -
                    {{ selectedRequest.section }}</span
                  >
                </div>
                <div class="info-row">
                  <span class="info-label">Home:</span>
                  <span class="info-value">{{
                    selectedRequest.homePhone || "-"
                  }}</span>
                </div>
                <div class="info-row">
                  <span class="info-label">Cell:</span>
                  <span class="info-value">{{
                    selectedRequest.cellPhone || "-"
                  }}</span>
                </div>
                <div class="info-row">
                  <span class="info-label">Bus Addr:</span>
                  <span class="info-value">{{
                    selectedRequest.busAddress1 || "-"
                  }}</span>
                </div>
                <div
                  class="info-row"
                  v-if="selectedRequest.notificationPhoneNumber"
                >
                  <span class="info-label">Notify:</span>
                  <span class="info-value">
                    {{ formatPhone(selectedRequest.notificationPhoneNumber) }}
                    <span
                      class="grey--text"
                      v-if="selectedRequest.notificationType"
                    >
                      ({{ selectedRequest.notificationType }})
                    </span>
                  </span>
                </div>
              </v-card>
            </v-flex>

            <!-- Right Column: Bus Change Form -->
            <v-flex xs12 md7 class="pl-2">
              <div class="section-header mb-2">
                <v-icon x-small class="mr-1">mdi-bus-marker</v-icon>
                Bus Change Details
              </div>

              <v-layout row wrap>
                <v-flex xs6 class="pr-1">
                  <v-menu
                    v-model="startDateMenu"
                    :close-on-content-click="false"
                    offset-y
                    min-width="290px"
                  >
                    <template v-slot:activator="{ on }">
                      <v-text-field
                        v-model="formData.startDate"
                        label="Start Date *"
                        prepend-inner-icon="mdi-calendar"
                        readonly
                        outlined
                        dense
                        hide-details
                        class="mb-2"
                        v-on="on"
                      ></v-text-field>
                    </template>
                    <v-date-picker
                      v-model="formData.startDate"
                      color="rgb(92, 184, 96)"
                      @input="startDateMenu = false"
                    ></v-date-picker>
                  </v-menu>
                </v-flex>
                <v-flex xs6 class="pl-1">
                  <v-menu
                    v-model="endDateMenu"
                    :close-on-content-click="false"
                    offset-y
                    min-width="290px"
                  >
                    <template v-slot:activator="{ on }">
                      <v-text-field
                        v-model="formData.endDate"
                        label="End Date"
                        prepend-inner-icon="mdi-calendar"
                        readonly
                        outlined
                        dense
                        hide-details
                        class="mb-2"
                        v-on="on"
                      ></v-text-field>
                    </template>
                    <v-date-picker
                      v-model="formData.endDate"
                      color="rgb(92, 184, 96)"
                      :min="formData.startDate"
                      @input="endDateMenu = false"
                    ></v-date-picker>
                  </v-menu>
                </v-flex>
              </v-layout>

              <v-text-field
                v-model="formData.busChangeAddress"
                label="Bus Change Address *"
                prepend-inner-icon="mdi-map-marker"
                outlined
                dense
                hide-details
                class="mb-2"
                autocomplete="off"
              ></v-text-field>

              <v-layout row wrap>
                <v-flex xs6 class="pr-1">
                  <v-text-field
                    v-model="formData.busChangePhoneNumber"
                    label="Phone"
                    prepend-inner-icon="mdi-phone"
                    outlined
                    dense
                    class="mb-0"
                    autocomplete="off"
                    :rules="[rules.phoneNumber]"
                    mask="###-###-####"
                  ></v-text-field>
                </v-flex>
                <v-flex xs6 class="pl-1">
                  <v-text-field
                    v-model="formData.busChangeName"
                    label="Contact Name"
                    prepend-inner-icon="mdi-account"
                    outlined
                    dense
                    hide-details
                    class="mb-0"
                    autocomplete="off"
                  ></v-text-field>
                </v-flex>
              </v-layout>

              <v-layout row wrap>
                <v-flex xs6 class="pr-1">
                  <v-text-field
                    v-model="formData.busChangeAccountID"
                    label="Account ID"
                    prepend-inner-icon="mdi-account-search"
                    outlined
                    dense
                    hide-details
                    class="mb-2"
                    readonly
                    @click="openAccountSearch"
                    style="cursor: pointer;"
                  ></v-text-field>
                </v-flex>
                <v-flex xs6 class="pl-1">
                  <v-autocomplete
                    v-model="selectedBus"
                    :items="filteredBuses"
                    item-text="busDescribtion"
                    item-value="busCode"
                    label="Bus Code"
                    prepend-inner-icon="mdi-bus"
                    outlined
                    dense
                    clearable
                    hide-no-data
                    hide-details
                    class="mb-2"
                    :loading="loadingBuses"
                    :search-input.sync="busSearch"
                    return-object
                    autocomplete="off"
                  ></v-autocomplete>
                </v-flex>
              </v-layout>

              <v-textarea
                v-model="formData.note"
                label="Note"
                prepend-inner-icon="mdi-note-text"
                outlined
                dense
                hide-details
                rows="2"
              ></v-textarea>
            </v-flex>
          </v-layout>
        </v-card-text>

        <v-divider></v-divider>

        <v-card-actions class="py-3 px-4">
          <v-spacer></v-spacer>
          <v-btn color="grey darken-1" dark @click="closeDialog">
            Cancel
          </v-btn>
          <v-btn
            color="success"
            dark
            :loading="saving"
            @click="saveRequest"
            class="save-btn"
          >
            <v-icon left>mdi-content-save</v-icon>
            Complete & Save
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Account Search Dialog -->
    <v-dialog v-model="accountSearchDialog" max-width="1400">
      <v-card>
        <data-grid
          class="accounts-search"
          url="accounts"
          :columns="accountsColumns"
          title="Search Accounts"
          navigationPath="Account View"
          navigationField="accountID"
          :hideActions="true"
          :onRowClicked="onAccountSelected"
        ></data-grid>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import moment from "moment";

export default {
  data: () => ({
    title: "Bus Change Requests",
    actions: [],
    url: "",

    // Dialog state
    processDialog: false,
    saving: false,
    selectedRequest: null,

    // Account search dialog
    accountSearchDialog: false,

    // Date pickers
    startDateMenu: false,
    endDateMenu: false,

    // Bus selection
    busesAll: [],
    filteredBuses: [],
    selectedBus: null,
    busSearch: null,
    loadingBuses: false,

    // Form data
    formData: {
      startDate: "",
      endDate: "",
      busChangeAddress: "",
      busChangePhoneNumber: "",
      busChangeName: "",
      busChangeAccountID: "",
      note: "",
    },

    // Validation rules
    rules: {
      phoneNumber: (v) => {
        if (!v) return true;
        const digits = v.replace(/[-\s()]/g, "");
        return (
          ([10, 11].includes(digits.length) && /^\d+$/.test(digits)) ||
          "Invalid Phone Number"
        );
      },
    },

    // Account search columns (same format as Apply.vue)
    accountsColumns: [
      {
        sortable: true,
        label: "Name",
        field: "name",
        hidden: false,
        width: "200px",
        filterOptions: {
          enabled: true,
          placeholder: "Search Name..",
        },
      },
      {
        sortable: false,
        label: "Account ID",
        field: "accountID",
        width: "115px",
        hidden: false,
        filterOptions: {
          enabled: true,
          placeholder: "Search Account ID..",
        },
      },
      {
        sortable: false,
        label: "Hebrew Name",
        width: "250px",
        field: "hname",
        hidden: false,
        filterOptions: {
          enabled: true,
          placeholder: "Search Hebrew Name..",
        },
      },
      {
        sortable: false,
        label: "Home Phone",
        field: "homePhone",
        width: "150px",
        hidden: false,
        searchValue: (value) => {
          return value.replace(/(\d{3})(\d{3})(\d{4})/, "$1-$2-$3");
        },
        filterOptions: {
          enabled: true,
          placeholder: "Search Home Phone..",
        },
      },
      {
        sortable: false,
        label: "Cellphone",
        field: "cellPhone",
        width: "150px",
        hidden: false,
        searchValue: (value) => {
          return value.replace(/(\d{3})(\d{3})(\d{4})/, "$1-$2-$3");
        },
        filterOptions: {
          enabled: true,
          placeholder: "Search Cellphone..",
        },
      },
      {
        sortable: false,
        label: "Address",
        field: "address1",
        width: "200px",
        hidden: false,
        filterOptions: {
          enabled: true,
          placeholder: "Search Address..",
        },
      },
      {
        sortable: false,
        label: "Email",
        field: "emailAddress",
        hidden: false,
        filterOptions: {
          enabled: true,
        },
      },
    ],

    columns: [
      {
        field: "statusIcon",
        label: "",
        isIcon: true,
        template: "icon",
        alignment: "center",
        visible: true,
        allowFiltering: false,
        allowHeaderFiltering: false,
        cssClass: "width-25",
        icon: (value) => {
          if (!value) return "";
          switch (value.trim().toUpperCase()) {
            case "PENDING":
              return "mdi-clock";
            case "COMPLETED":
              return "mdi-check-circle";
            default:
              return "mdi-help-circle";
          }
        },
        iconColor: (value) => {
          if (!value) return "";
          switch (value.trim().toUpperCase()) {
            case "PENDING":
              return "#ff9800";
            case "COMPLETED":
              return "rgb(92, 184, 96)";
            default:
              return "#9e9e9e";
          }
        },
      },
      {
        label: "Status",
        field: "status",
        visible: true,
        filterValues: [],
        filterValue: null,
        selectedFilterOperation: null,
        sortIndex: 0,
        sortOrder: "asc",
        calculateSortValue: (data) => {
          const order = { PENDING: 1, COMPLETED: 2 };
          const status = data.status ? data.status.toUpperCase() : "";
          return order[status] || 3;
        },
      },
      {
        label: "Date",
        field: "date",
        visible: true,
        type: "date",
        format: "MM/dd/yyyy hh:mm a",
        filterValues: [],
        filterValue: null,
        selectedFilterOperation: null,
        sortIndex: 1,
        sortOrder: "desc",
      },
      {
        label: "Student ID",
        field: "studentID",
        visible: true,
        filterValues: [],
        filterValue: null,
        selectedFilterOperation: null,
      },
      {
        label: "Student Name",
        field: "studentName",
        visible: true,
        filterValues: [],
        filterValue: null,
        selectedFilterOperation: null,
      },
      {
        label: "Student Name (Heb)",
        field: "studentNameHeb",
        visible: true,
        filterValues: [],
        filterValue: null,
        selectedFilterOperation: null,
      },
      {
        label: "Family ID",
        field: "familyID",
        visible: true,
        filterValues: [],
        filterValue: null,
        selectedFilterOperation: null,
      },
      {
        label: "Building",
        field: "building",
        visible: true,
        filterValues: [],
        filterValue: null,
        selectedFilterOperation: null,
      },
      {
        label: "Branch",
        field: "branch",
        visible: true,
        filterValues: [],
        filterValue: null,
        selectedFilterOperation: null,
      },
      {
        label: "Class",
        field: "class",
        visible: true,
        filterValues: [],
        filterValue: null,
        selectedFilterOperation: null,
      },
      {
        label: "Section",
        field: "section",
        visible: true,
        filterValues: [],
        filterValue: null,
        selectedFilterOperation: null,
      },
      {
        label: "Caller ID",
        field: "callerID",
        visible: true,
        filterValues: [],
        filterValue: null,
        selectedFilterOperation: null,
      },
      {
        label: "Home Phone",
        field: "homePhone",
        visible: false,
        filterValues: [],
        filterValue: null,
        selectedFilterOperation: null,
      },
      {
        label: "Cell Phone",
        field: "cellPhone",
        visible: false,
        filterValues: [],
        filterValue: null,
        selectedFilterOperation: null,
      },
      {
        label: "Bus Address",
        field: "busAddress1",
        visible: true,
        filterValues: [],
        filterValue: null,
        selectedFilterOperation: null,
      },
      {
        label: "Notification Phone",
        field: "notificationPhoneNumber",
        visible: false,
        filterValues: [],
        filterValue: null,
        selectedFilterOperation: null,
      },
      {
        label: "Notification Type",
        field: "notificationType",
        visible: false,
        filterValues: [],
        filterValue: null,
        selectedFilterOperation: null,
      },
      {
        label: "Completed By",
        field: "completedBy",
        visible: true,
        filterValues: [],
        filterValue: null,
        selectedFilterOperation: null,
      },
      {
        label: "Completed On",
        field: "completedOn",
        visible: true,
        type: "date",
        format: "MM/dd/yyyy hh:mm a",
        filterValues: [],
        filterValue: null,
        selectedFilterOperation: null,
      },
    ],
  }),

  computed: {
    userBranch() {
      const branch = this.$store.state.authentication.branch;
      if (branch && !branch.includes("null")) {
        return branch;
      }
      return null;
    },
    getRecordingUrl() {
      if (!this.selectedRequest) return null;
      return (
        this.selectedRequest.recordingURL ||
        this.selectedRequest.RecordingURL ||
        this.selectedRequest.recordingUrl ||
        null
      );
    },
  },

  watch: {
    busSearch(value) {
      if (!value) {
        this.filteredBuses = this.busesAll;
        return;
      }
      if (!this.busesAll.length) return;
      this.filteredBuses = this.busesAll.filter((b) =>
        b.busDescribtion.toUpperCase().includes(value.toUpperCase())
      );
    },
  },

  methods: {
    async beforeSetupGrid() {
      const branchParam = this.userBranch ? this.userBranch : "";
      this.url =
        "busChangeRequests?branch=" + branchParam + "&includeCompleted=";

      this.actions.push({
        text: "Show Completed",
        type: "switch",
        align: "left",
        action: this.onShowCompletedChanged,
      });
    },

    onShowCompletedChanged(value) {
      const branchParam = this.userBranch ? this.userBranch : "";
      if (value) {
        this.url =
          "busChangeRequests?branch=" + branchParam + "&includeCompleted=true";
      } else {
        this.url =
          "busChangeRequests?branch=" + branchParam + "&includeCompleted=";
      }
      this.$refs.grid.reloadDataSource(this.url);
    },

    async onRowClicked(row) {
      this.selectedRequest = row;
      this.resetForm();

      // Load buses if not loaded
      if (!this.busesAll.length) {
        await this.loadBuses();
      }

      this.processDialog = true;
    },

    resetForm() {
      this.formData = {
        startDate: "",
        endDate: "",
        busChangeAddress: "",
        busChangePhoneNumber: "",
        busChangeName: "",
        busChangeAccountID: "",
        note: "",
      };
      this.selectedBus = null;
      this.busSearch = null;
    },

    closeDialog() {
      this.processDialog = false;
      this.selectedRequest = null;
      this.resetForm();
    },

    async loadBuses() {
      this.loadingBuses = true;
      try {
        this.busesAll = await this.$http.get("api/Students/GetBuses");
        this.filteredBuses = this.busesAll;
      } catch (e) {
        this.busesAll = [];
        this.filteredBuses = [];
      } finally {
        this.loadingBuses = false;
      }
    },

    formatDate(date) {
      if (!date) return "";
      return moment(date).format("MM/DD/YYYY hh:mm A");
    },

    formatPhone(phone) {
      if (!phone) return null;
      const digits = phone.replace(/\D/g, "");
      if (digits.length === 10) {
        return digits.replace(/(\d{3})(\d{3})(\d{4})/, "$1-$2-$3");
      } else if (digits.length === 11 && digits.startsWith("1")) {
        return digits.replace(/(\d{1})(\d{3})(\d{3})(\d{4})/, "$1-$2-$3-$4");
      }
      return phone;
    },

    openAccountSearch() {
      this.accountSearchDialog = true;
    },

    onAccountSelected(account) {
      this.accountSearchDialog = false;
      this.formData.busChangeAccountID = account.accountID;
      // Use name field if available, otherwise combine lastName + firstName
      this.formData.busChangeName =
        account.name ||
        ((account.lastName || "") + " " + (account.firstName || "")).trim();
      if (account.address1) {
        this.formData.busChangeAddress = account.address1;
      }
    },

    validateForm() {
      if (!this.formData.startDate) {
        this.$notify({
          text: "Start Date is required",
          group: "notification",
          type: "error",
        });
        return false;
      }

      if (!this.formData.busChangeAddress) {
        this.$notify({
          text: "Bus Change Address is required",
          group: "notification",
          type: "error",
        });
        return false;
      }

      // Validate phone number if provided
      if (this.formData.busChangePhoneNumber) {
        const phoneValidation = this.rules.phoneNumber(
          this.formData.busChangePhoneNumber
        );
        if (phoneValidation !== true) {
          this.$notify({
            text: phoneValidation,
            group: "notification",
            type: "error",
          });
          return false;
        }
      }

      return true;
    },

    async saveRequest() {
      if (!this.validateForm()) return;

      this.saving = true;
      try {
        const data = {
          busChangeRequestID: this.selectedRequest.id,
          enteredBy: this.$store.state.authentication.user,
          startDate: this.formData.startDate
            ? moment(this.formData.startDate).format("MM/DD/YYYY")
            : null,
          endDate: this.formData.endDate
            ? moment(this.formData.endDate).format("MM/DD/YYYY")
            : null,
          note: this.formData.note,
          studentID: this.selectedRequest.studentID,
          homeAddress: this.selectedRequest.busAddress1 || "",
          busChangeAddress: this.formData.busChangeAddress,
          building: this.selectedRequest.building,
          branch: this.selectedRequest.branch,
          class: this.selectedRequest.class,
          section: this.selectedRequest.section,
          term: this.selectedRequest.regID,
          busChangePhoneNumber: this.formData.busChangePhoneNumber,
          busChangeName: this.formData.busChangeName,
          busChangeAccountID: this.formData.busChangeAccountID,
          busCode: this.selectedBus ? this.selectedBus.busCode : null,
        };

        const response = await this.$http.post(
          "api/BusChangeRequests/convertToBusChange",
          data
        );

        if (response.error) {
          return this.$notify({
            group: "notification",
            type: "error",
            text: response.error,
          });
        }

        this.$notify({
          text: "Bus change request completed successfully",
          group: "notification",
          type: "success",
        });

        this.closeDialog();
        this.$refs.grid.reloadGrid();
      } catch (e) {
        this.$notify({
          text: "Failed to save bus change request",
          group: "notification",
          type: "error",
        });
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<style scoped>
.request-dialog .dialog-header {
  background: #5cb860;
  color: white;
}

.request-dialog .dialog-title {
  font-size: 1.25rem;
  font-weight: 500;
}

.section-header {
  display: flex;
  align-items: center;
  font-size: 0.75rem;
  font-weight: 600;
  color: #424242;
  padding-bottom: 4px;
  border-bottom: 1px solid #e0e0e0;
}

.recording-card {
  background-color: #fafafa;
}

.audio-player {
  width: 100%;
  min-height: 40px;
  display: block;
}

.compact-info {
  font-size: 0.8rem;
}

.info-row {
  display: flex;
  padding: 2px 0;
  border-bottom: 1px solid #f5f5f5;
}

.info-row:last-child {
  border-bottom: none;
}

.info-label {
  font-size: 0.75rem;
  color: #757575;
  min-width: 70px;
  flex-shrink: 0;
}

.info-value {
  font-size: 0.8rem;
  font-weight: 500;
  color: #212121;
}

.save-btn {
  min-width: 140px;
}
</style>
